/**
 * Resize Shopify CDN product images for storefront thumbs/cards.
 * Raw /cart.js and product APIs often return full originals (multi‑MB);
 * native Shopify UIs request ~100–400px derivatives instead.
 */

export const CART_LINE_THUMB_DISPLAY_PX = 64;
/** 2× retina for the 64px cart line thumb. */
export const CART_LINE_THUMB_CDN_WIDTH = 128;
/** Default 2× width for catalog/upsell product cards (~160–200px display). */
export const CATALOG_CARD_CDN_WIDTH = 360;
/** Default 2× width for small gallery thumbs (~44px display). */
export const CATALOG_THUMB_CDN_WIDTH = 96;

function isShopifyCdnUrl(url) {
  try {
    const host = url.hostname.toLowerCase();
    const path = url.pathname.toLowerCase();
    return (
      host === "cdn.shopify.com" ||
      host.endsWith(".myshopify.com") ||
      path.includes("/cdn/shop/") ||
      path.includes("/s/files/")
    );
  } catch {
    return false;
  }
}

/**
 * @param {unknown} displayPx — CSS display size in px
 * @param {number} [fallback]
 * @returns {number}
 */
export function resolveShopifyCdnWidthFromDisplay(displayPx, fallback = CATALOG_CARD_CDN_WIDTH) {
  const floor = Math.max(
    64,
    Math.round(Number(fallback) || CATALOG_CARD_CDN_WIDTH)
  );
  const n = Number(displayPx);
  if (!Number.isFinite(n) || n <= 0) {
    return Math.min(1200, floor);
  }
  // Never request smaller than the catalog floor — schema columnWidth can be
  // ~72px while product-page cards render ~170–340px wide.
  return Math.min(1200, Math.max(floor, Math.round(n * 2)));
}

/**
 * @param {unknown} src
 * @param {number} [width]
 * @returns {string}
 */
export function getShopifyCdnSizedImageUrl(src, width = CATALOG_CARD_CDN_WIDTH) {
  if (src == null) return "";
  let raw = String(src).trim();
  if (!raw) return "";
  if (raw.startsWith("//")) raw = `https:${raw}`;

  const size = Math.max(
    16,
    Math.min(2048, Math.round(Number(width) || CATALOG_CARD_CDN_WIDTH))
  );

  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://cdn.shopify.com";
    const url = new URL(raw, base);
    if (!isShopifyCdnUrl(url)) return raw;

    url.searchParams.set("width", String(size));
    return url.toString();
  } catch {
    return raw;
  }
}

/**
 * @param {unknown} item — Shopify /cart.js line item
 * @param {number} [width]
 * @returns {string}
 */
export function getCartLineItemThumbUrl(item, width = CART_LINE_THUMB_CDN_WIDTH) {
  if (!item || typeof item !== "object") return "";
  const featured =
    item.featured_image && typeof item.featured_image === "object"
      ? item.featured_image.url || item.featured_image.src
      : null;
  return getShopifyCdnSizedImageUrl(item.image || featured || "", width);
}

/**
 * Fire-and-forget browser decode of cart line thumbs (same URL as <img src>).
 * @param {object | null | undefined} cart
 * @param {number} [width]
 */
export function preloadCartLineThumbImages(cart, width = CART_LINE_THUMB_CDN_WIDTH) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  for (const item of items) {
    const src = getCartLineItemThumbUrl(item, width);
    if (!src || typeof Image === "undefined") continue;
    const image = new Image();
    image.decoding = "async";
    image.src = src;
  }
}

/**
 * Preload a list of product image URLs (already sized or raw).
 * @param {string[]} urls
 * @param {number} [width] — applied when sizing raw Shopify URLs
 */
export function preloadShopifyProductImages(urls, width = CATALOG_CARD_CDN_WIDTH) {
  if (!Array.isArray(urls) || typeof Image === "undefined") return;
  for (const raw of urls) {
    const src = getShopifyCdnSizedImageUrl(raw, width);
    if (!src) continue;
    const image = new Image();
    image.decoding = "async";
    image.src = src;
  }
}
